import { ProductType } from '@/types';
import { getParams } from './params';
import { trackError } from './raygun';

export type Restriction = [number, number]; // width in mm, height in mm

const defaultRestrictions: Restriction = [210, 210];
const restriction40x70: Restriction = [40, 70];
const restriction70x70: Restriction = [70, 70];
const restriction75x44: Restriction = [75, 44];
const restriction75x75: Restriction = [75, 75];
const restriction100x100: Restriction = [100, 100];
const restriction105x105: Restriction = [105, 105];
const restriction110x60: Restriction = [110, 60];
const restriction110x110: Restriction = [110, 110];
const restriction148x148: Restriction = [148, 148];
const restriction120x50: Restriction = [120, 50];
const restriction155x155: Restriction = [155, 155];
const restriction200x100: Restriction = [200, 100];

const restrictionsCap = { 1: restriction120x50 };
const restrictionsHat = { 1: restriction110x60 };
const restrictionsSmallFront = { 1: restriction100x100 };
const restrictionsSmallFrontAndBack = { 1: restriction100x100, 2: restriction100x100 };
const restrictionsTShirt = { 1: restriction110x110, 2: restriction200x100 };
const restrictionsHoodie = { 1: restriction105x105 };

const embroideryRestrictions: {
  EU: {
    [productTypeId: string]: {
      [viewId: string]: Restriction;
    };
  };
  NA: {
    [productTypeId: string]: {
      [viewId: string]: Restriction;
    };
  };
} = {
  EU: {
    /* Winter Hat */ 51: restrictionsHat,
    /* Men's Premium Hooded Jacket */ 93: restrictionsSmallFront,
    /* [inactive] Men's Retro T-Shirt */ 144: restrictionsTShirt,
    /* Organic Baby Bibs */ 235: restrictionsSmallFront,
    /* Flexfit Baseball Cap */ 428: restrictionsCap,
    /* Women's Premium Hooded Jacket */ 445: restrictionsSmallFront,
    /* Teddy Bear */ 623: { 1: restriction40x70, 2: restriction40x70 },
    /* Men's Ringer Shirt */ 646: restrictionsTShirt,
    /* Kids' Premium Hoodie */ 654: restrictionsSmallFront,
    /* Baby Organic T-Shirt with Round Neck */ 664: restrictionsSmallFront,
    /* Kids' Premium Hooded Jacket */ 678: { 1: restriction70x70 },
    /* Women's Ringer T-Shirt */ 718: restrictionsTShirt,
    /* Contrast Snapback Cap */ 803: restrictionsCap,
    /* Kids' Baseball T-Shirt */ 824: restrictionsSmallFront,
    /* Snapback Cap */ 905: restrictionsCap,
    /* Trucker Cap */ 1040: restrictionsCap,
    /* Women's Hooded Fleece Jacket */ 1054: restrictionsHoodie,
    /* Jersey Beanie */ 1089: restrictionsHat,
    /* [inactive] Bobble Hat */ 1095: restrictionsHat,
    /* Men's Workwear T-Shirt */ 1217: restrictionsTShirt,
    /* Men's Gildan Heavy T-Shirt */ 1219: restrictionsTShirt,
    /* Women's Gildan Heavy T-Shirt */ 1220: restrictionsTShirt,
    /* Kids T-Shirt by Russell */ 1222: restrictionsSmallFront,
    /* Teenager Heavy Cotton T-Shirt */ 1223: restrictionsSmallFront,
    /* Unisex Heavyweight Hooded Jacket */ 1242: restrictionsHoodie,
    /* Baseball Jacket */ 1254: restrictionsSmallFront,
    /* Women's Organic T-Shirt by Stanley & Stella */ 1264: restrictionsTShirt,
    /* Men's Organic T-Shirt by Stanley & Stella */ 1265: restrictionsTShirt,
    /* Men's Polo Shirt */ 1266: restrictionsSmallFront,
    /* Women's Polo Shirt */ 1267: restrictionsSmallFront,
    /* Men's Tipped Polo Shirt */ 1280: restrictionsSmallFront,
    /* Women's Tipped Polo Shirt */ 1283: restrictionsSmallFront,
    /* Classic Women's V-Neck T-Shirt */ 1329: restrictionsTShirt,
    /* Men's V-Neck T-Shirt */ 1330: restrictionsTShirt,
    /* Kids' Premium Sweatshirt */ 1434: restrictionsSmallFront,
    /* Organic Pouch */ 1444: restrictionsSmallFrontAndBack,
    /* Unisex sweater with zip collar */ 1467: restrictionsSmallFront,
    /* Unisex Joggers */ 1475: { 1: restriction148x148, 2: restriction148x148 },
    /* College Sweat Jacket */ 1964: restrictionsHoodie,
  },
  NA: {
    /* Baseball Cap */ 129: restrictionsCap,
    /* Men's Zip Hoodie */ 198: restrictionsHoodie,
    /* Kids‘ Premium Hoodie */ 654: restrictionsHoodie,
    /* Unisex Fleece Zip Hoodie */ 669: { 1: restriction75x75 },
    /* Snapback Baseball Cap */ 803: restrictionsCap,
    /* Knit Cap with Cuff Print */ 945: { 1: restriction75x44 },
    /* Trucker Cap */ 1040: restrictionsCap,
    /* Adjustable Apron */ 1186: { 1: restriction155x155 },
    /* Bucket Hat */ 1462: { 1: restriction120x50, 2: restriction120x50 },
    /* Organic Cadet Cap */ 1587: restrictionsCap,
    /* Organic Baseball Cap */ 1588: restrictionsCap,
  },
};

export function getRestrictions(productType: ProductType, printAreaId: string): Restriction {
  const { id: productTypeId, hoopFrames } = productType;
  const viewForPrintArea = productType.printAreas.find((pa) => pa.id === printAreaId)?.defaultView
    .id;
  const hoopFrameForPrintArea =
    hoopFrames && hoopFrames.find((frame) => frame.printAreaId === printAreaId);

  if (hoopFrameForPrintArea) {
    return [hoopFrameForPrintArea.width, hoopFrameForPrintArea.height];
  } else if (viewForPrintArea) {
    const platform = getParams().platform;
    const staticPtRestrictions = embroideryRestrictions[platform][productTypeId];

    if (
      staticPtRestrictions &&
      staticPtRestrictions[viewForPrintArea] &&
      !getParams().useGlobalInventoryData
    ) {
      trackError('Missing assortment system hoopframe', {
        productTypeId: productType.id,
        printAreaId: printAreaId,
      });
      console.warn(
        `Hoopframes for product type ${productType.id} and print area ${printAreaId} not defined in assortment system. Falling back to static values.`
      );
    }

    return staticPtRestrictions?.[viewForPrintArea] || defaultRestrictions;
  }

  return defaultRestrictions;
}
